/**
 * Game Type Utility Functions
 * 
 * This module provides utilities for working with game types from the database.
 * It centralizes game type logic and capabilities while maintaining the database
 * as the single source of truth.
 */

import { supabase } from './supabase';

// Cache for game types to avoid repeated database queries
let gameTypesCache = null;
let lastFetchTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all game types from the database
 * @returns {Promise<Array>} Array of game type objects
 */
export const fetchGameTypes = async () => {
  const now = Date.now();
  
  // Use cache if available and not expired
  if (gameTypesCache && (now - lastFetchTime) < CACHE_TTL) {
    return gameTypesCache;
  }
  
  // Fetch from database
  const { data, error } = await supabase
    .from('game_types')
    .select('*');
    
  if (error) {
    console.error('Error fetching game types:', error);
    return gameTypesCache || []; // Return cache if available, empty array otherwise
  }
  
  // Update cache
  gameTypesCache = data;
  lastFetchTime = now;
  
  return data;
};

/**
 * Get game type by ID
 * @param {number} id - The game type ID to find
 * @returns {Promise<Object|null>} The game type object or null if not found
 */
export const getGameTypeById = async (id) => {
  const types = await fetchGameTypes();
  return types.find(type => type.id === id) || null;
};

/**
 * Get game type by name
 * @param {string} name - The game type name to find
 * @returns {Promise<Object|null>} The game type object or null if not found
 */
export const getGameTypeByName = async (name) => {
  const types = await fetchGameTypes();
  return types.find(type => type.name === name) || null;
};

/**
 * Game type capabilities configuration
 * This maps game type IDs to their capabilities
 */
export const GAME_TYPE_CAPABILITIES = {
  // Default capabilities for any game type
  DEFAULT: {
    isDoubles: true,
    requiresEvenTeams: true,
    requiresGenderBalance: false,
    maxPlayers: 4
  },
  
  // Mixed Doubles
  1: {
    isDoubles: true,
    requiresEvenTeams: true,
    requiresGenderBalance: true,
    maxPlayers: 4,
    minMalePerTeam: 1,
    minFemalePerTeam: 1
  },
  
  // Mens Doubles
  2: {
    isDoubles: true,
    requiresEvenTeams: true,
    requiresGenderBalance: false,
    maxPlayers: 4,
    allowedGenders: ['male']
  },
  
  // Womens Doubles
  3: {
    isDoubles: true,
    requiresEvenTeams: true,
    requiresGenderBalance: false,
    maxPlayers: 4,
    allowedGenders: ['female']
  },
  
  // Mens Singles
  4: {
    isDoubles: false,
    requiresEvenTeams: true,
    requiresGenderBalance: false,
    maxPlayers: 2,
    allowedGenders: ['male']
  },
  
  // Womens Singles
  5: {
    isDoubles: false,
    requiresEvenTeams: true,
    requiresGenderBalance: false,
    maxPlayers: 2,
    allowedGenders: ['female']
  },
  
  // Mens vs Mixed Doubles (formerly Unbalanced Doubles)
  6: {
    isDoubles: true,
    requiresEvenTeams: false,
    requiresGenderBalance: false,
    maxPlayers: 4,
    affectsElo: false // Unbalanced games don't affect ELO
  },
  
  // Unbalanced Singles
  7: {
    isDoubles: false,
    requiresEvenTeams: false,
    requiresGenderBalance: false,
    maxPlayers: 2
  },
  
  // Womens vs Mixed Doubles
  8: {
    isDoubles: true,
    requiresEvenTeams: false,
    requiresGenderBalance: false,
    maxPlayers: 4,
    affectsElo: false // Unbalanced games don't affect ELO
  },
  
  // Mens vs Womens Doubles
  9: {
    isDoubles: true,
    requiresEvenTeams: false,
    requiresGenderBalance: false,
    maxPlayers: 4,
    affectsElo: false // Unbalanced games don't affect ELO
  }
};

/**
 * Get capabilities for a game type
 * @param {number} gameTypeId - The ID of the game type
 * @returns {Object} The capabilities object for the game type
 */
export const getGameTypeCapabilities = (gameTypeId) => {
  return GAME_TYPE_CAPABILITIES[gameTypeId] || GAME_TYPE_CAPABILITIES.DEFAULT;
};

/**
 * Determines the game type based on player genders and team composition
 * This is a more advanced version of the original detectGameType function
 * that uses the database game types and capabilities
 * 
 * @param {Array} team1Players - Array of player objects for team 1
 * @param {Array} team2Players - Array of player objects for team 2
 * @returns {Object} The detected game type with id and name
 */
export const detectGameType = (team1Players, team2Players) => {
  // Validate input
  if (!team1Players || !team2Players) {
    console.warn('Invalid team data provided to detectGameType');
    return { id: 6, name: 'Unbalanced Doubles' }; // Default to Unbalanced Doubles
  }

  const allPlayers = [...team1Players, ...team2Players];
  const totalPlayers = allPlayers.length;

  // Check if this is a singles or doubles match
  if (totalPlayers === 2) {
    // Singles match
    const player1 = allPlayers[0];
    const player2 = allPlayers[1];
    
    if (!player1.gender || !player2.gender) {
      return { id: 7, name: 'Unbalanced Singles' }; // Unbalanced Singles (unknown gender)
    }
    
    // Normalize gender values to lowercase
    const gender1 = player1.gender.toLowerCase();
    const gender2 = player2.gender.toLowerCase();
    
    if (gender1 === 'male' && gender2 === 'male') {
      return { id: 4, name: 'Mens Singles' }; // Mens Singles
    } else if (gender1 === 'female' && gender2 === 'female') {
      return { id: 5, name: 'Womens Singles' }; // Womens Singles
    } else {
      return { id: 7, name: 'Unbalanced Singles' }; // Unbalanced Singles (mixed gender in singles)
    }
  } else if (totalPlayers === 4) {
    // Doubles match - normalize gender values to lowercase
    const team1Genders = team1Players.map(p => p.gender ? p.gender.toLowerCase() : null).filter(Boolean);
    const team2Genders = team2Players.map(p => p.gender ? p.gender.toLowerCase() : null).filter(Boolean);
    
    // Check if we have valid gender data for all players
    if (team1Genders.length !== 2 || team2Genders.length !== 2) {
      console.warn('Missing gender data for some players:', { team1Genders, team2Genders, team1Players, team2Players });
      return { id: 6, name: 'Unbalanced Doubles' }; // Unbalanced Doubles (missing gender data)
    }
    
    // Check for mixed doubles FIRST (each team has one male and one female)
    // This must be checked before other types to correctly identify mixed doubles
    const team1HasMixed = team1Genders.includes('male') && team1Genders.includes('female');
    const team2HasMixed = team2Genders.includes('male') && team2Genders.includes('female');
    
    if (team1HasMixed && team2HasMixed) {
      return { id: 1, name: 'Mixed Doubles' }; // Mixed Doubles
    }
    
    // Check for mens doubles (all players are male)
    const allMale = allPlayers.every(p => {
      const gender = p.gender ? p.gender.toLowerCase() : null;
      return gender === 'male';
    });
    if (allMale) {
      return { id: 2, name: 'Mens Doubles' }; // Mens Doubles
    }
    
    // Check for womens doubles (all players are female)
    const allFemale = allPlayers.every(p => {
      const gender = p.gender ? p.gender.toLowerCase() : null;
      return gender === 'female';
    });
    if (allFemale) {
      return { id: 3, name: 'Womens Doubles' }; // Womens Doubles
    }
    
    // Check for unbalanced doubles (one all-gender team vs mixed team)
    const team1AllMale = team1Genders.every(g => g === 'male');
    const team2AllMale = team2Genders.every(g => g === 'male');
    const team1AllFemale = team1Genders.every(g => g === 'female');
    const team2AllFemale = team2Genders.every(g => g === 'female');
    
    // Mens vs Mixed: One team is all male, other is mixed
    if ((team1AllMale && team2HasMixed) || (team2AllMale && team1HasMixed)) {
      return { id: 6, name: 'Mens vs Mixed Doubles' };
    }
    
    // Womens vs Mixed: One team is all female, other is mixed
    if ((team1AllFemale && team2HasMixed) || (team2AllFemale && team1HasMixed)) {
      return { id: 8, name: 'Womens vs Mixed Doubles' };
    }
    
    // Mens vs Womens: One team is all male, other is all female
    if ((team1AllMale && team2AllFemale) || (team2AllMale && team1AllFemale)) {
      return { id: 9, name: 'Mens vs Womens Doubles' };
    }
    
    // If we get here, it's an unbalanced doubles match (fallback to Mens vs Mixed)
    return { id: 6, name: 'Mens vs Mixed Doubles' }; // Default unbalanced
  } else {
    // Invalid number of players
    console.warn(`Invalid number of players: ${totalPlayers}`);
    return { id: 6, name: 'Unbalanced Doubles' }; // Default to Unbalanced Doubles
  }
};

/**
 * Gets a human-readable description of the game type
 * @param {number} gameTypeId - The game_type_id
 * @returns {Promise<string>} Human-readable description
 */
export const getGameTypeDescription = async (gameTypeId) => {
  const gameType = await getGameTypeById(gameTypeId);
  return gameType?.description || 'Unknown Game Type';
};

/**
 * Validates if a game type is valid for the given number of players
 * @param {number} gameTypeId - The game_type_id
 * @param {number} playerCount - Number of players in the game
 * @returns {boolean} True if valid, false otherwise
 */
export const isValidGameTypeForPlayerCount = (gameTypeId, playerCount) => {
  const capabilities = getGameTypeCapabilities(gameTypeId);
  return playerCount <= capabilities.maxPlayers;
};

/**
 * Checks if a game type is a doubles game
 * @param {number} gameTypeId - The game_type_id
 * @returns {boolean} True if doubles, false if singles
 */
export const isDoublesGameType = (gameTypeId) => {
  const capabilities = getGameTypeCapabilities(gameTypeId);
  return capabilities.isDoubles;
};

/**
 * Checks if a game type requires gender balance
 * @param {number} gameTypeId - The game_type_id
 * @returns {boolean} True if gender balance is required
 */
export const requiresGenderBalance = (gameTypeId) => {
  const capabilities = getGameTypeCapabilities(gameTypeId);
  return capabilities.requiresGenderBalance;
};

/**
 * Checks if a game type is an unbalanced game (doesn't affect ELO)
 * @param {number} gameTypeId - The game_type_id
 * @returns {boolean} True if this is an unbalanced game type (IDs 6 or 8)
 */
export const isUnbalancedGameType = (gameTypeId) => {
  return gameTypeId === 6 || gameTypeId === 8;
};

/**
 * Checks if a game type affects ELO ratings
 * @param {number} gameTypeId - The game_type_id
 * @returns {boolean} True if this game type affects ELO ratings
 */
export const affectsElo = (gameTypeId) => {
  const capabilities = getGameTypeCapabilities(gameTypeId);
  return capabilities.affectsElo !== false; // Default to true if not specified
};
